import { AxiosError } from 'axios'
import toast from 'react-hot-toast'

const API_UNAVAILABLE_AR =
  'تعذّر الاتصال بخادم النظام (API). تأكد من تشغيل السيرفر ثم أعد المحاولة.'
const API_UNAVAILABLE_EN =
  'Cannot connect to the API server. Please ensure the server is running and try again.'

const GENERIC_MESSAGE =
  /^(حدث خطأ|خطأ في التحقق|تم العثور على\s+\d+|an error occurred\b|invalid request data$|one or more validation errors occurred|request failed with status code\b|validation error\b|found \d+ account validation|internal server error$|the request is invalid\b|see the inner exception)/i

export type UiLanguage = 'ar' | 'en' | 'fr'

export function readUiLanguage(): UiLanguage {
  try {
    if (typeof localStorage === 'undefined') return 'ar'
    const raw = localStorage.getItem('language-storage')
    if (!raw) return 'ar'
    const lang = JSON.parse(raw)?.state?.language
    if (lang === 'en' || lang === 'fr' || lang === 'ar') return lang
  } catch {
    /* ignore malformed storage */
  }
  return 'ar'
}

function hasArabic(text: string): boolean {
  return /[\u0600-\u06FF]/.test(text)
}

function isGenericMessage(text: string): boolean {
  const trimmed = text.trim()
  if (!trimmed) return true
  return GENERIC_MESSAGE.test(trimmed)
}

function isStackTrace(text: string): boolean {
  return text.includes('   at ') || text.includes('\n   at ') || text.includes('\r\n   at ')
}

function clip(text: string): string {
  const trimmed = text.trim()
  if (trimmed.length <= 700) return trimmed
  return `${trimmed.slice(0, 700).trim()}…`
}

function asText(value: unknown): string {
  return typeof value === 'string' ? value.trim() : ''
}

function splitBilingual(text: string): { en: string; ar: string } {
  if (!text || isStackTrace(text)) return { en: '', ar: '' }
  const parts = text.split(' | ').map((part) => part.trim()).filter(Boolean)
  if (parts.length >= 2) {
    const ar = parts.find((part) => hasArabic(part)) ?? ''
    const en = parts.find((part) => !hasArabic(part)) ?? ''
    return { en: clip(en || parts[0]), ar: clip(ar) }
  }
  const single = clip(text)
  return hasArabic(single) ? { en: '', ar: single } : { en: single, ar: '' }
}

function lineText(value: unknown, language: string): string {
  if (typeof value === 'string') return value.trim()
  if (!value || typeof value !== 'object') return ''
  const row = value as Record<string, unknown>
  const ar = asText(row.messageAr) || asText(row.MessageAr)
  const en = asText(row.message) || asText(row.Message)
  if (language === 'ar') return ar || en
  return en || ar
}

function accountErrorText(data: Record<string, unknown>, language: string): string {
  const errors = data.accountErrors ?? data.AccountErrors
  if (!Array.isArray(errors) || errors.length === 0) return ''
  const lines = errors.map((row) => lineText(row, language)).filter((line) => line && !isGenericMessage(line))
  return lines.join('\n')
}

function validationText(errors: unknown, language: string): string {
  if (!errors) return ''
  if (typeof errors === 'string') return errors.trim()
  if (Array.isArray(errors)) {
    return errors
      .map((row) => lineText(row, language))
      .filter((line) => line && !isGenericMessage(line))
      .join('\n')
  }
  if (typeof errors === 'object') {
    return Object.values(errors as Record<string, unknown>)
      .flatMap((value) => (Array.isArray(value) ? value : [value]))
      .map((value) => (typeof value === 'string' ? value.trim() : lineText(value, language)))
      .filter((line) => line && !isGenericMessage(line))
      .join('\n')
  }
  return ''
}

function formatQty(raw: string): string {
  const value = Number(raw)
  if (!Number.isFinite(value)) return raw
  return String(parseFloat(value.toFixed(4)))
}

/** Turns the English stock exception into a sentence an Arabic user can read. */
function localizeEnglishStock(text: string, language: string): string {
  if (language !== 'ar' || hasArabic(text)) return text
  const match = text.match(
    /Insufficient stock\b[\s\S]*?Available:\s*([0-9]+(?:\.[0-9]+)?)[\s\S]*?(?:Requested|Required):\s*([0-9]+(?:\.[0-9]+)?)/i
  )
  if (!match) return text
  const available = formatQty(match[1])
  const requested = formatQty(match[2])
  const item = text.match(/item\s+'([^']+)'/i)?.[1]?.trim()
  const store = text.match(/store\s+'([^']+)'/i)?.[1]?.trim()
  const itemPart = item ? ` للصنف «${item}»` : ''
  const storePart = store ? ` في المخزن «${store}»` : ''
  return `المخزون غير كافٍ${itemPart}${storePart}. المتوفر ${available}، والمطلوب ${requested}.`
}

function firstSpecific(candidates: string[]): string {
  for (const candidate of candidates) {
    const text = candidate.trim()
    if (!text || isGenericMessage(text) || isStackTrace(text)) continue
    return clip(text)
  }
  return ''
}

/** Best sentence to show the user, taken from message / messageAr / error / accountErrors / validation errors. */
export function pickApiErrorMessage(data: unknown, language: string = readUiLanguage()): string {
  if (typeof data === 'string') {
    const split = splitBilingual(data)
    const picked = language === 'ar' ? split.ar || split.en : split.en || split.ar
    return localizeEnglishStock(picked, language)
  }
  if (!data || typeof data !== 'object') return ''

  const body = data as Record<string, unknown>
  const account = accountErrorText(body, language)
  if (account) return account

  const message = asText(body.message) || asText(body.Message)
  const messageAr = asText(body.messageAr) || asText(body.MessageAr)
  const messageEn = asText(body.messageEn) || asText(body.MessageEn)
  const title = asText(body.title) || asText(body.Title)
  const rawDetail = asText(body.error) || asText(body.Error) || asText(body.detail) || asText(body.Detail)
    || asText(body.innerException) || asText(body.InnerException)
  const detail = splitBilingual(rawDetail)
  const messageSplit = splitBilingual(message)
  const validation = validationText(body.errors ?? body.Errors, language)

  if (language === 'ar') {
    return localizeEnglishStock(firstSpecific([
      messageAr,
      detail.ar,
      messageSplit.ar,
      hasArabic(validation) ? validation : '',
      hasArabic(message) ? message : '',
      message,
      detail.en,
      validation,
      title,
    ]), language)
  }

  return firstSpecific([
    messageEn,
    !hasArabic(message) ? message : '',
    detail.en,
    messageSplit.en,
    !hasArabic(validation) ? validation : '',
    messageAr,
    detail.ar,
    validation,
    title,
  ])
}

function shouldReplaceMessage(current: string, next: string, language: string): boolean {
  const replacement = next.trim()
  if (!replacement || replacement === current.trim()) return false
  if (isGenericMessage(current)) return true
  if (language === 'ar' && hasArabic(replacement) && !hasArabic(current)) return true
  if (language !== 'ar' && !hasArabic(replacement) && hasArabic(current)) return true
  return false
}

/**
 * Rewrites API error bodies so existing screens that read `message` show the
 * real failure reason (parent account, validation, and so on) instead of a generic wrapper.
 */
export function enrichAxiosError(error: AxiosError): void {
  if (!error.response) return
  const language = readUiLanguage()
  const data = error.response.data

  if (typeof data === 'string') {
    const picked = pickApiErrorMessage(data, language)
    if (!picked) return
    error.response.data = {
      message: picked,
      messageAr: pickApiErrorMessage(data, 'ar') || picked,
      error: data,
    }
    error.message = picked
    return
  }

  if (!data || typeof data !== 'object') return
  const body = data as Record<string, unknown>
  const picked = pickApiErrorMessage(body, language)
  const pickedAr = pickApiErrorMessage(body, 'ar')
  const pickedEn = pickApiErrorMessage(body, 'en')
  const current = asText(body.message)
  const currentAr = asText(body.messageAr)

  if (picked && shouldReplaceMessage(current, picked, language)) {
    if (body.messageEn == null && pickedEn && !hasArabic(pickedEn)) body.messageEn = pickedEn
    body.message = picked
  }
  if (pickedAr && shouldReplaceMessage(currentAr, pickedAr, 'ar')) {
    body.messageAr = pickedAr
  }

  const display = asText(body.message) || picked
  if (display && (isGenericMessage(error.message) || shouldReplaceMessage(current, display, language))) {
    error.message = display
  }
}

export function isApiServerUnavailable(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const err = error as AxiosError & { isApiUnavailable?: boolean }
  if (err.isApiUnavailable) return true

  // No HTTP response usually means the server is unreachable or offline.
  if (!err.response) {
    const code = err.code ?? ''
    if (
      code === 'ERR_NETWORK' ||
      code === 'ECONNREFUSED' ||
      code === 'ERR_CONNECTION_REFUSED' ||
      code === 'ECONNABORTED'
    ) {
      return true
    }

    const message = (err.message ?? '').toLowerCase()
    if (
      message === 'network error' ||
      message.includes('failed to fetch') ||
      message.includes('connection refused') ||
      message.includes('err_connection_refused') ||
      message.includes('net::err_connection_refused')
    ) {
      return true
    }
  }

  return false
}

export function getApiErrorMessage(
  error: unknown,
  options?: {
    language?: 'ar' | 'en' | string
    fallbackAr?: string
    fallbackEn?: string
  }
): string {
  const language = options?.language ?? readUiLanguage()
  const arabicUi = language !== 'en' && language !== 'fr'

  if (isApiServerUnavailable(error)) {
    return arabicUi ? API_UNAVAILABLE_AR : API_UNAVAILABLE_EN
  }

  const axiosError = error as AxiosError
  const apiMessage = pickApiErrorMessage(axiosError.response?.data, language)
  if (apiMessage) return apiMessage

  if (error instanceof Error && error.message && !isGenericMessage(error.message)) return error.message

  return arabicUi
    ? options?.fallbackAr ?? 'حدث خطأ غير متوقع'
    : options?.fallbackEn ?? 'An unexpected error occurred'
}

/** Every user-facing sentence in an API body, without duplicates. */
export function collectApiMessages(data: unknown, language: string = readUiLanguage()): string[] {
  if (data == null) return []
  const lines: string[] = []
  const push = (value: string) => {
    const text = clip(value)
    if (!text || isStackTrace(text) || isGenericMessage(text)) return
    if (lines.includes(text)) return
    lines.push(text)
  }

  const primary = pickApiErrorMessage(data, language)
  if (primary) push(primary)

  if (typeof data === 'object') {
    const body = data as Record<string, unknown>
    const warnings = (body.warnings ?? body.Warnings) as Record<string, unknown> | undefined
    const warningMessages = warnings?.messages ?? warnings?.Messages
    if (Array.isArray(warningMessages)) {
      warningMessages.forEach((row) => {
        if (typeof row === 'string') push(row)
      })
    }
    const validation = validationText(body.errors ?? body.Errors, language)
    if (validation) validation.split('\n').forEach(push)
    const accounts = accountErrorText(body, language)
    if (accounts) accounts.split('\n').forEach(push)
  }

  return lines
}

/** Shows each message returned by the API. Falls back when the body has none. */
export function toastApiMessages(
  data: unknown,
  language: string,
  fallback: string,
  kind: 'success' | 'error' = 'success'
) {
  const lines = collectApiMessages(data, language)
  const show = kind === 'error' ? toast.error : toast.success
  if (lines.length === 0) {
    show(fallback)
    return
  }
  lines.forEach((line) => show(line, { duration: kind === 'error' ? 8000 : 6000 }))
}

/** Formats API error response body for toast notifications. */
export function formatApiErrorForToast(responseData: unknown, fallback: string, language?: string): string {
  const picked = pickApiErrorMessage(responseData, language ?? readUiLanguage())
  return picked || fallback
}
